import { Observable } from 'rxjs/Observable';
import _ from 'lodash';

import { SmartenitPlugin } from '../smartenit-plugin';
import { IActive } from '../../interfaces/active.interface';
import { IWebSocketDeviceMessage } from '../../websockets/websocket-device-message.interface';

const CACHE_TIME: number = -1;

export class IASZoneServerPlugin extends SmartenitPlugin implements IActive {
  private _type: any;
  private _zoneId: number;
  private _cieParent: string;
  private _enrolled: boolean;

  public static readonly TYPES: any = {
    '0x0000': {
      alarm1: 'System Alarm',
      alarm2: '',
      type: 'Standard CIE'
    },
    '0x000d': {
      condition: {
        activeMessage: 'Motion',
        activeMessageTranslation: 'AR.STATES.MOTION',
        idleMessage: 'No Motion',
        idleMessageTranslation: 'AR.STATES.NO_MOTION'
      },
      state: {
        activeMessage: 'MOTION',
        activeMessageTranslation: 'DEVICES.IAS.STATE.MOTION',
        idleMessage: 'NO MOTION',
        idleMessageTranslation: 'DEVICES.IAS.STATE.NO_MOTION'
      },
      type: 'Motion sensor'
    },
    '0x0015': {
      condition: {
        activeMessage: 'is Open',
        activeMessageTranslation: 'AR.CONDITIONS_NL.IS_OPEN',
        idleMessage: 'is Closed',
        idleMessageTranslation: 'AR.CONDITIONS_NL.IS_CLOSED'
      },
      state: {
        activeMessage: 'OPEN',
        activeMessageTranslation: 'DEVICES.IAS.STATE.OPEN',
        idleMessage: 'CLOSED',
        idleMessageTranslation: 'DEVICES.IAS.STATE.CLOSED'
      },
      type: 'Contact switch'
    },
    '0x0028': {
      condition: {
        activeMessage: 'Fire Detected',
        activeMessageTranslation: 'AR.STATES.FIRE_DETECTED',
        idleMessage: 'No Fire',
        idleMessageTranslation: 'AR.STATES.NO_FIRE'
      },
      state: {
        activeMessage: 'FIRE DETECTED',
        activeMessageTranslation: 'DEVICES.IAS.STATE.FIRE_DETECTED',
        idleMessage: 'NO FIRE',
        idleMessageTranslation: 'DEVICES.IAS.STATE.NO_FIRE'
      },
      type: 'Fire sensor'
    },
    '0x002a': {
      bitmask: 3,
      condition: {
        activeMessage: 'Leak Detected',
        activeMessageTranslation: 'AR.STATES.LEAK_DETECTED',
        idleMessage: 'No Leak',
        idleMessageTranslation: 'AR.STATES.NO_LEAK'
      },
      state: {
        activeMessage: 'LEAK',
        activeMessageTranslation: 'DEVICES.IAS.STATE.LEAK',
        idleMessage: 'NO LEAK',
        idleMessageTranslation: 'DEVICES.IAS.STATE.NO_LEAK'
      },
      type: 'Water sensor'
    },
    '0x002b': {
      condition: {
        activeMessage: 'CO Detected',
        activeMessageTranslation: 'AR.STATES.CO_DETECTED',
        idleMessage: 'No CO',
        idleMessageTranslation: 'AR.STATES.NO_CO'
      },
      state: {
        activeMessage: 'CO DETECTED',
        activeMessageTranslation: 'DEVICES.IAS.STATE.CO_DETECTED',
        idleMessage: 'NO CO',
        idleMessageTranslation: 'DEVICES.IAS.STATE.NO_CO'
      },
      type: 'Carbon Monoxide (CO) sensor'
    },
    '0x002c': {
      condition: {
        activeMessage: 'Emergency Detected',
        activeMessageTranslation: 'AR.STATES.EMERGENCY_DETECTED',
        idleMessage: 'No Emergency',
        idleMessageTranslation: 'AR.STATES.NO_EMERGENCY'
      },
      state: {
        activeMessage: 'EMERGENCY',
        activeMessageTranslation: 'DEVICES.IAS.STATE.EMERGENCY',
        idleMessage: 'NO EMERGENCY',
        idleMessageTranslation: 'DEVICES.IAS.STATE.NO_EMERGENCY'
      },
      type: 'Personal emergency device'
    },
    '0x002d': {
      condition: {
        activeMessage: 'Vibration Detected',
        activeMessageTranslation: 'AR.STATES.VIBRATION_DETECTED',
        idleMessage: 'No Vibration',
        idleMessageTranslation: 'AR.STATES.NO_VIBRATION'
      },
      state: {
        activeMessage: 'VIBRATION DETECTED',
        activeMessageTranslation: 'DEVICES.IAS.STATE.VIBRATION_DETECTED',
        idleMessage: 'NO VIBRATION',
        idleMessageTranslation: 'DEVICES.IAS.STATE.NO_VIBRATION'
      },
      type: 'Vibration/Movement sensor'
    },
    '0x010f': {
      alarm1: 'Panic',
      alarm2: 'Emergency',
      type: 'Remote control'
    },
    '0x0115': {
      alarm1: 'Panic',
      alarm2: 'Emergency',
      type: 'Key fob'
    },
    '0x021d': {
      alarm1: 'Panic',
      alarm2: 'Emergency',
      type: 'Keypad'
    },
    '0x0225': {
      alarm1: '',
      alarm2: '',
      type: 'Standard warning device'
    },
    '0x0226': {
      condition: {
        activeMessage: 'Glass Break Detected',
        activeMessageTranslation: 'AR.STATES.GLASS_BREAK_DETECTED',
        idleMessage: 'No Glass Break',
        idleMessageTranslation: 'AR.STATES.NO_GLASS_BREAK'
      },
      state: {
        activeMessage: 'GLASS BREAK DETECTED',
        activeMessageTranslation: 'DEVICES.IAS.STATE.GLASS_BREAK_DETECTED',
        idleMessage: 'NO GLASS BREAK',
        idleMessageTranslation: 'DEVICES.IAS.STATE.NO_GLASS_BREAK'
      },
      type: 'Glass break sensor'
    },
    '0x0229': {
      alarm1: '',
      alarm2: '',
      type: 'Security repeater'
    }
  };

  public onInit() {
    this.getCacheZoneValues();
  }

  get type(): any {
    return this._type;
  }

  get cieParent(): string {
    return this._cieParent;
  }

  public belongsToPanel(panelHwId: string): boolean {
    return panelHwId === this._cieParent;
  }

  public setType(value: any) {
    if (IASZoneServerPlugin.TYPES.hasOwnProperty(value)) {
      this._type = IASZoneServerPlugin.TYPES[value];
      this.setCache('type', this._type, CACHE_TIME);
    }
  }

  public getStateMessage(context: any = null) {
    const type =
      this._type && this._type.state
        ? this._type.state
        : { activeMessageTranslation: 'DEVICES.IAS.STATE.ACTIVE', idleMessageTranslation: 'DEVICES.IAS.STATE.IDLE' };
    return this.isActive() ? type.activeMessageTranslation : type.idleMessageTranslation;
  }

  get zoneId(): number {
    return this._zoneId;
  }

  public isActive(): boolean {
    return (
      (this.state && this.state.alarm1 && this.state.alarm1.value) ||
      (this.state && this.state.alarm2 && this.state.alarm2.value)
    );
  }

  public isEnrolled(): boolean {
    return this._enrolled;
  }

  public updatedAt(): Date {
    return this.state && this.state.sensed_at
      ? this.state.sensed_at
      : new Date();
  }

  private parseStatus(status: number) {
    const _status: number = 0xffff & status;
    const mask: number = 0x0001;
    const rsp: any = {
      ac_main: {
        string: (_status >> 7) & mask ? 'AC/Mains fault' : 'AC/Mains OK',
        value: (_status >> 7) & mask
      },
      alarm1: {
        string: _status & mask ? 'opened/alarmed' : 'closed/not alarmed',
        value: _status & mask
      },
      alarm2: {
        string: (_status >> 1) & mask ? 'opened/alarmed' : 'closed/not alarmed',
        value: (_status >> 1) & mask
      },
      battery: {
        string: (_status >> 3) & mask ? 'Low battery' : 'Battery Ok',
        value: (_status >> 3) & mask
      },
      battery_defect: {
        string:
          (_status >> 9) & mask
            ? 'Sensor detects a defective battery'
            : 'Sensor battery is functioning normally',
        value: (_status >> 9) & mask
      },
      sensed_at: new Date(),
      tamper: {
        string: (_status >> 2) & mask ? 'Tampered' : 'Not tampered',
        value: (_status >> 2) & mask
      },
      test: {
        string:
          (_status >> 8) & mask
            ? 'Sensor is in test mode'
            : 'Sensor is in operation mode',
        value: (_status >> 8) & mask
      },
      trouble: {
        string: (_status >> 6) & mask ? 'Trouble/Failure' : 'OK',
        value: (_status >> 6) & mask
      }
    };

    return rsp;
  }

  public getStatusPayload(): any {
    return ['ZoneStatusChangeNotification'];
  }

  public getStatus(
    context?: string,
    force?: boolean,
    asObservable?: boolean,
    payload?: any,
    sendDirect?: boolean
  ) {
    super.getStatus(context, true, asObservable, payload, sendDirect);
  }

  public processMessage(
    message: IWebSocketDeviceMessage,
    timestamp?: number
  ): any {
    const attribute = message && message.attributeOrMethod;

    const response = message && message.data && message.data.response;
    const value = response && response.value;

    if (attribute === 'ZoneStatus') {
      this.state = this.parseStatus(parseInt(response.value));
      this.saveState(this.state, CACHE_TIME, timestamp);
      this._onUpdate.next({ state: this.state });
    } else if (attribute === 'ZoneStatusChangeNotification') {
      this.state = this.parseStatus(parseInt(response.value.ZoneStatus));
      this.saveState(this.state, CACHE_TIME, timestamp);
      this._onUpdate.next({ state: this.state });
    } else if (
      attribute === 'ZoneType' &&
      IASZoneServerPlugin.TYPES.hasOwnProperty(response.value)
    ) {
      this._type = IASZoneServerPlugin.TYPES[response.value];
      this.setCache('type', this._type, CACHE_TIME, undefined, timestamp);
      this._onUpdate.next({ type: this._type });
    } else if (attribute === 'ZoneID') {
      this._zoneId = response.value;
      this.setCache('zoneId', this._zoneId, CACHE_TIME, undefined, timestamp);
      this._onUpdate.next({ zoneId: this._zoneId });
    } else if (attribute === 'IAS_CIE_Address') {
      this._cieParent =
        'IASACE-' + response.value.replace(/0x/g, '').toLowerCase();
      this.setCache(
        'cieParent',
        this._cieParent,
        CACHE_TIME,
        undefined,
        timestamp
      );
      this._onUpdate.next({ cieParent: this._cieParent });
    } else if (attribute === 'ZoneState') {
      this._enrolled = response.value === '0x01' ? true : false;
      this.setCache(
        'enrolled',
        this._enrolled,
        CACHE_TIME,
        undefined,
        timestamp
      );
      this._onUpdate.next({ enrolled: this._enrolled });
    }

    return this.state;
  }

  private getCacheZoneValues() {
    const cacheValues = [
      this.getCache('zoneId'),
      this.getCache('type'),
      this.getCache('cieParent'),
      this.getCache('enrolled'),
      this.getCache()
    ];

    Observable.forkJoin(cacheValues).subscribe((array) => {
      if (array && array[0] != null) {
        this._zoneId = array[0];
      } else {
        this._zoneId = 0;
      }

      if (array && array[1] != null) {
        this._type = array[1];
      } else {
        this._type = {};
      }

      if (array && array[2] != null) {
        this._cieParent = array[2];
      } else {
        this._cieParent = '';
      }

      if (array && array[3] != null) {
        this._enrolled = array[3];
      } else {
        this._enrolled = false;
      }

      if (array && array[4] != null) {
        this.state = array[4];
      } else {
        this.state = {};
      }

      this._onUpdate.next({
        cieParent: this._cieParent,
        enrolled: this._enrolled,
        state: this.state,
        type: this._type,
        zoneId: this._zoneId
      });
    });
  }

  public getZoneType(asObservable?: boolean) {
    if (asObservable) {
      const payload = {};
      const query = `timeout=3&cacheTTL=${this.stateTTL}`;
      return this.getAttributeWithSubscribeOption(
        this.componentId,
        this.processorName,
        'ZoneType',
        payload,
        true,
        query
      );
    } else {
      this.getAttributeWithSubscribeOption(
        this.componentId,
        this.processorName,
        'ZoneType',
        {},
        false,
        `cacheTTL=${this.stateTTL}`
      );
    }
  }

  public getZoneId(asObservable?: boolean) {
    if (asObservable) {
      const payload = {};
      const query = `timeout=3&cacheTTL=${this.stateTTL}`;
      return this.getAttributeWithSubscribeOption(
        this.componentId,
        this.processorName,
        'ZoneID',
        payload,
        true,
        query
      );
    } else {
      this.getAttributeWithSubscribeOption(
        this.componentId,
        this.processorName,
        'ZoneID',
        {},
        false,
        `cacheTTL=${this.stateTTL}`
      );
    }
  }

  public getZoneStatus(asObservable?: boolean) {
    // const attribute = 'ZoneStatus';
    const attribute = 'ZoneStatusChangeNotification';
    if (asObservable) {
      const payload = {};
      const query = `timeout=3&cacheTTL=${this.stateTTL}`;
      return this.getAttributeWithSubscribeOption(
        this.componentId,
        this.processorName,
        attribute,
        payload,
        true,
        query
      );
    } else {
      this.getAttributeWithSubscribeOption(
        this.componentId,
        this.processorName,
        attribute,
        {},
        false,
        `cacheTTL=${this.stateTTL}`
      );
    }
  }

  public getCIE(asObservable?: boolean) {
    if (asObservable) {
      const payload = {};
      const query = `timeout=3&cacheTTL=${this.stateTTL}`;
      return this.getAttributeWithSubscribeOption(
        this.componentId,
        this.processorName,
        'IAS_CIE_Address',
        payload,
        true,
        query
      );
    } else {
      this.getAttributeWithSubscribeOption(
        this.componentId,
        this.processorName,
        'IAS_CIE_Address',
        {},
        false,
        `cacheTTL=${this.stateTTL}`
      );
    }
  }

  public getEnrolledState(asObservable?: boolean) {
    if (asObservable) {
      const payload = {};
      const query = `timeout=3&cacheTTL=${this.stateTTL}`;
      return this.getAttributeWithSubscribeOption(
        this.componentId,
        this.processorName,
        'ZoneState',
        payload,
        true,
        query
      );
    } else {
      this.getAttributeWithSubscribeOption(
        this.componentId,
        this.processorName,
        'ZoneState',
        {},
        false,
        `cacheTTL=${this.stateTTL}`
      );
    }
  }

  public getConditionOptions(context: any | null = null): any {
    if (context && context.deviceType) {
      this.setType(context.deviceType);
    }

    return [
      {
        displayName:
          this._type &&
          this._type.condition &&
          this._type.condition.activeMessage
            ? this._type.condition.activeMessageTranslation
              ? _.get(
                  this.translationService.getResource(),
                  this._type.condition.activeMessageTranslation,
                  ''
                )
              : this._type.condition.activeMessage
            : _.get(
                this.translationService.getResource(),
                'AR.CONDITIONS_NL.ALARMED',
                ''
              ),
        method: {
          attribute: 'ZoneStatus',
          name: 'ZoneStatusChangeNotification'
        },
        name: 'Alarmed',
        value: { '#bitmask': 3 }
      },
      {
        displayName:
          this._type && this._type.condition && this._type.condition.idleMessage
            ? this._type.condition.idleMessageTranslation
              ? _.get(
                  this.translationService.getResource(),
                  this._type.condition.idleMessageTranslation,
                  ''
                )
              : this._type.condition.idleMessage
            : _.get(
                this.translationService.getResource(),
                'AR.CONDITIONS_NL.IDLE',
                ''
              ),
        method: {
          attribute: 'ZoneStatus',
          name: 'ZoneStatusChangeNotification'
        },
        name: 'Idle',
        value: { '#bitmask': 3, '#expected': 0 }
      }
    ];
  }

  public getConditionAttribute(): any {
    return null;
  }
}
